import React from "react";
import { AlertTriangle, ArrowLeft, ArrowRight, ArrowUpRight, BoxIcon } from "lucide-react";
import { focusRing, glassPanel, primaryButton } from "../../utils/demoUi";
import { demoNotices } from "./DemoShell";
export type ChooserCard = {
  id: string;
  title: string;
  body: string;
  buttonLabel: string;
  icon: BoxIcon;
  onEnter?: () => void;
  /** External destination, used when a domain's workspaces are hosted elsewhere. */
  href?: string;
};
type WorkspaceChooserProps = {
  domainId: string;
  eyebrow: string;
  title: string;
  description: string;
  cards: ChooserCard[];
  persistenceOk?: boolean;
  footnote?: React.ReactNode;
};
export function WorkspaceChooser({
  domainId,
  eyebrow,
  title,
  description,
  cards,
  persistenceOk = true,
  footnote
}: WorkspaceChooserProps) {
  return <main className="min-h-screen bg-[#F3F5FA] px-4 py-10 sm:px-6 lg:py-16" data-id="element-508" data-mp="34z4o8yivanqk">
      <div className="mx-auto max-w-5xl" data-id="element-509" data-mp="30136cgx6gugo">
        <a href={`/noetivra/demo-center#${domainId}`} className={`inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-navy transition-colors duration-150 ${focusRing}`} data-id="element-510" data-mp="300bcpbn556t1">
          <ArrowLeft size={16} aria-hidden="true" data-id="element-511" data-mp="cqadtkbu0uux" />
          Back to Demo Center
        </a>

        <div className="mt-12 max-w-2xl" data-id="element-512" data-mp="hs9gst3ntprc">
          <p className="text-sm font-semibold tracking-wide text-lavender" data-id="element-513" data-mp="hp3pd77k6bsg">{eyebrow}</p>
          <h1 className="mt-3 font-heading text-4xl font-bold leading-tight tracking-tight text-ink-dark lg:text-5xl" data-id="element-514" data-mp="lqfivntjzh5x">
            {title}
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-ink-muted" data-id="element-515" data-mp="3q7t8hnoon04a">{description}</p>
        </div>

        <h2 className="sr-only" data-id="element-516" data-mp="3u7ad6ewabt8e">Choose a workspace</h2>
        <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2" data-id="element-517" data-mp="2tnu4yv8s9uh">
          {cards.map(card => {
          const Icon = card.icon;
          return <li key={card.id} data-id="element-518" data-mp="32g4b3sx07lgh">
                <article className={`${glassPanel} flex h-full flex-col p-7 lg:p-8`} data-id="element-519" data-mp="32gxb8b1gjcpn">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-lavender-pale text-lavender" data-id="element-520" data-mp="36h7q6d789dgn">
                    <Icon size={24} aria-hidden="true" data-id="element-521" data-mp="dlxq5z8qpqx2" />
                  </span>
                  <h3 className="mt-6 font-heading text-2xl font-bold text-ink-dark" data-id="element-522" data-mp="8oxkhpfsyq6h">{card.title}</h3>
                  <p className="mt-3 leading-relaxed text-ink-muted" data-id="element-523" data-mp="ndah8ompsd21">{card.body}</p>
                  <div className="mt-auto pt-8" data-id="element-524" data-mp="ifiro19jl310">
                    {card.href ? <a href={card.href} target="_blank" rel="noopener noreferrer" className={`${primaryButton} w-full`} data-id="element-525" data-mp="3qualtfheq5l1">
                        {card.buttonLabel}
                        <ArrowUpRight size={18} aria-hidden="true" data-id="element-526" data-mp="3ly29sayiasi3" />
                        <span className="sr-only" data-id="element-527" data-mp="3rnyykvjaynx">(opens in a new tab)</span>
                      </a> : <button type="button" onClick={card.onEnter} className={`${primaryButton} w-full`} data-id="element-528" data-mp="3vsu6lck9lbbt">
                        {card.buttonLabel}
                        <ArrowRight size={18} aria-hidden="true" data-id="element-529" data-mp="1f748nj8r6qd0" />
                      </button>}
                  </div>
                </article>
              </li>;
        })}
        </ul>

        {footnote && <div className="mt-8" data-id="element-530" data-mp="1ipjs6dk9b06j">{footnote}</div>}

        <div className="mt-10 border-t border-line pt-6 text-sm text-ink-muted" data-id="element-531" data-mp="1nnbjb5d2ui9o">
          {demoNotices.map(notice => <p key={notice} data-id="element-532" data-mp="18qb23vsii5pg">{notice}</p>)}
          {!persistenceOk && <p role="status" className="mt-3 flex items-center gap-2 text-amber-900" data-id="element-533" data-mp="1dnufyulasggv">
              <AlertTriangle size={16} aria-hidden="true" data-id="element-534" data-mp="21dosfb1pg30l" />
              Browser storage is unavailable. Changes are available only for the current session.
            </p>}
        </div>
      </div>
    </main>;
}